import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, HeartPulse, Loader2, Check, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API_BASE } from "@/lib/api";

const QUESTIONS = [
  {
    id: "energy",
    title: "How would you rate your daily energy levels?",
    options: [
      { label: "Running on empty most days", points: 1 },
      { label: "Up and down — depends on the day", points: 2 },
      { label: "Mostly steady", points: 3 },
      { label: "Energised and consistent", points: 4 },
    ],
  },
  {
    id: "sleep",
    title: "How many hours of sleep do you average?",
    options: [
      { label: "Less than 5 hours", points: 1 },
      { label: "5–6 hours", points: 2 },
      { label: "7–8 hours", points: 3 },
      { label: "8+ restful hours", points: 4 },
    ],
  },
  {
    id: "movement",
    title: "How often do you move or exercise each week?",
    options: [
      { label: "Rarely — life gets in the way", points: 1 },
      { label: "1–2 sessions", points: 2 },
      { label: "3–4 sessions", points: 3 },
      { label: "5+ sessions", points: 4 },
    ],
  },
  {
    id: "stress",
    title: "How would you describe your stress levels?",
    options: [
      { label: "Overwhelmed most of the time", points: 1 },
      { label: "Often stressed", points: 2 },
      { label: "Manageable with effort", points: 3 },
      { label: "Calm and in control", points: 4 },
    ],
  },
  {
    id: "nutrition",
    title: "How balanced is your daily nutrition?",
    options: [
      { label: "Mostly processed or skipped meals", points: 1 },
      { label: "Inconsistent — good days and bad", points: 2 },
      { label: "Fairly balanced", points: 3 },
      { label: "Clean, whole-food based", points: 4 },
    ],
  },
  {
    id: "goal",
    title: "What is your primary wellness goal right now?",
    scored: false,
    options: [
      { label: "Lose weight & feel lighter", points: 0 },
      { label: "Build strength & fitness", points: 0 },
      { label: "Reduce stress & sleep better", points: 0 },
      { label: "Boost daily energy & focus", points: 0 },
    ],
  },
];

const FOCUS_TIPS = {
  energy: "Start your day with 10 minutes of movement and a glass of water before caffeine — small anchors compound into all-day energy.",
  sleep: "Build a 30-minute wind-down ritual: screens off, lights low, light stretching. Sleep is the foundation everything else is built on.",
  movement: "Begin with three 30-minute sessions a week — consistency beats intensity. Your body adapts to what you repeat, not what you attempt.",
  stress: "Practise 5 minutes of slow breathing (4s in, 6s out) daily. Managing the nervous system is as trainable as any muscle.",
  nutrition: "Anchor each meal around protein and one colourful vegetable. Simple frameworks outlast strict diets every time.",
};

const categoryFor = (score) =>
  score >= 16
    ? { name: "Strong Foundation", note: "You have solid habits — now it's about optimising and levelling up." }
    : score >= 11
    ? { name: "Building Momentum", note: "You're on the right path. A few targeted shifts will unlock the next level." }
    : { name: "Time for a Reset", note: "Your body is asking for attention — and small, consistent steps will change everything." };

export default function WellnessQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("quiz");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const score = QUESTIONS.filter((q) => q.scored !== false).reduce(
    (s, q) => s + (answers[q.id]?.points || 0),
    0
  );
  const category = categoryFor(score);
  const focusAreas = QUESTIONS.filter((q) => q.scored !== false && (answers[q.id]?.points || 0) <= 2)
    .map((q) => q.id)
    .slice(0, 3);
  const tips = (focusAreas.length ? focusAreas : ["movement"]).map((f) => FOCUS_TIPS[f]);

  const pick = (q, opt) => {
    setAnswers((a) => ({ ...a, [q.id]: opt }));
    setTimeout(() => {
      if (step < QUESTIONS.length - 1) setStep(step + 1);
      else setPhase("capture");
    }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/wellness/lead`, {
        name,
        email,
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, v.label])),
        score,
        category: category.name,
      });
      setPhase("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const q = QUESTIONS[step];

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#89937C]/15 flex items-center justify-center">
            <HeartPulse size={18} className="text-[#89937C]" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2D372B]">Wellness Assessment</h1>
        </div>
        <p className="text-[#5C665A] mb-10">
          Answer {QUESTIONS.length} quick questions and unlock a personalised wellness plan built around your lifestyle.
        </p>

        {phase === "quiz" && (
          <div data-testid="wellness-quiz-card" className="bg-white border border-[#E5E2D9] rounded-3xl p-8">
            <div className="flex items-center justify-between mb-6">
              <span className="overline">Question {step + 1} of {QUESTIONS.length}</span>
              <div className="flex gap-1.5">
                {QUESTIONS.map((_, i) => (
                  <span key={i} className={`h-1.5 w-6 rounded-full ${i <= step ? "bg-[#89937C]" : "bg-[#E5E2D9]"}`} />
                ))}
              </div>
            </div>
            <h2 data-testid="wellness-question-title" className="font-serif-display text-2xl md:text-3xl text-[#2D372B] mb-6">{q.title}</h2>
            <div className="space-y-3">
              {q.options.map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  data-testid={`wellness-option-${q.id}-${opt.points}`}
                  onClick={() => pick(q, opt)}
                  className={`w-full text-left px-5 py-4 rounded-2xl border transition-all ${
                    answers[q.id]?.label === opt.label
                      ? "border-[#89937C] ring-2 ring-[#89937C]/30 bg-[#89937C]/5"
                      : "border-[#E5E2D9] hover:border-[#89937C]/60"
                  } text-[#2D372B]`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {step > 0 && (
              <button type="button" data-testid="wellness-back-btn" onClick={() => setStep(step - 1)} className="mt-6 text-sm text-[#5C665A] hover:text-[#2D372B] inline-flex items-center gap-1">
                <ArrowLeft size={14} /> Previous question
              </button>
            )}
          </div>
        )}

        {phase === "capture" && (
          <form onSubmit={submit} data-testid="wellness-capture-card" className="bg-white border border-[#E5E2D9] rounded-3xl p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
              <Sparkles size={22} className="text-[#89937C]" />
            </div>
            <h2 className="font-serif-display text-3xl text-[#2D372B] mb-2">Your plan is ready</h2>
            <p className="text-sm text-[#5C665A] mb-6">
              We've analysed your answers. Enter your details to unlock your personalised wellness plan.
            </p>
            <div className="space-y-3 max-w-sm mx-auto text-left">
              <Input data-testid="wellness-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
              <Input data-testid="wellness-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" className="bg-[#F7F5F0] border-[#E5E2D9] h-12" />
            </div>
            <button
              data-testid="wellness-unlock-btn"
              type="submit"
              disabled={loading}
              className="mt-6 w-full max-w-sm inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              Unlock my personalized wellness plan
            </button>
            <p className="mt-4 text-xs text-[#5C665A]">No spam — just your plan and an occasional note from Coach Sanjay.</p>
          </form>
        )}

        {phase === "done" && (
          <div data-testid="wellness-result-card" className="bg-white border border-[#E5E2D9] rounded-3xl p-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 mx-auto rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
                <Check size={22} className="text-[#89937C]" />
              </div>
              <div className="overline">Your Wellness Score</div>
              <div data-testid="wellness-score" className="font-serif-display text-6xl text-[#2D372B] mt-2">
                {score}<span className="text-2xl text-[#5C665A]">/20</span>
              </div>
              <div className="mt-2 inline-block text-sm px-4 py-1.5 rounded-full bg-[#89937C]/15 text-[#2D372B] font-medium">
                {category.name}
              </div>
              <p className="text-sm text-[#5C665A] mt-3 max-w-md mx-auto">{category.note}</p>
            </div>
            <div className="overline mb-3">Your first steps</div>
            <div className="space-y-3">
              {tips.map((t, i) => (
                <div key={i} className="flex gap-3 bg-[#F7F5F0] rounded-2xl p-4">
                  <span className="font-serif-display text-lg text-[#89937C] shrink-0">{i + 1}.</span>
                  <p className="text-sm text-[#5C665A]">{t}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 p-5 bg-[#2D372B] rounded-2xl text-center">
              <p className="text-sm text-[#C9CFC0]">
                Thanks {name.split(" ")[0]} — your full personalised plan is on its way to{" "}
                <span className="text-[#F7F5F0] font-medium">{email}</span>. Coach Sanjay will follow up
                personally to help you put it into action.
              </p>
              <Link to="/bootcamps" data-testid="wellness-cta-bootcamps" className="mt-4 inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 bg-[#F7F5F0] text-[#2D372B] text-sm font-medium hover:bg-[#C9CFC0] transition-colors">
                Explore upcoming bootcamps
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
